import ChatIcon from '@mui/icons-material/Chat';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatClearIcon from '@mui/icons-material/FormatClear';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import LayersIcon from '@mui/icons-material/Layers';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SaveIcon from '@mui/icons-material/Save';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import TranslateIcon from '@mui/icons-material/Translate';
import UndoIcon from '@mui/icons-material/Undo';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import {
    Alert,
    AppBar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Drawer,
    Fab,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    List,
    ListItem,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Popover,
    Select,
    Tab,
    Tabs,
    TextField,
    Toolbar,
    Tooltip,
    Typography,
} from '@mui/material';
import { debounce } from 'lodash';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { v4 as uuidv4 } from 'uuid';
import { useAutoSave } from '../../hooks/useAutoSave';
import { handleCaretPreservingInput, useCaretPreservingHtmlSync } from '../../hooks/useCaretPreservingContentEditable';
import EditSessionService from '../../services/EditSessionService';
import textFileServiceInstance from '../../services/TextFileService';
import { useTrackUsage } from '../../hooks/useTrackUsage';
import AppConfig from '../../utils/AppConfig';
import { getAuthHeaders, hasWriteAccess, isAdmin } from '../../utils/authUtils';
import { textEditorHelpItems } from '../../utils/helpItems';
import { htmlToDocxBlob, htmlToPdfBlob, htmlToPlainText } from '../../utils/textFileDownloadUtils';
import HelpOverlay from '../common/HelpOverlay';
import { PageHeader } from '../common/PageHeader';
import ModernBreadcrumbs from '../navigation/ModernBreadcrumbs';

// Languages supported by the translation service
const SUPPORTED_LANGUAGES = [
    { code: 'English', name: 'English' },
    { code: 'Gujarati', name: 'Gujarati' },
    { code: 'Hindi', name: 'Hindi' },
    { code: 'Spanish', name: 'Spanish' },
    { code: 'French', name: 'French' },
    { code: 'German', name: 'German' },
    { code: 'Russian', name: 'Russian' },
    { code: 'Mandarin', name: 'Mandarin' },
    { code: 'Portuguese', name: 'Portuguese' },
    { code: 'Korean', name: 'Korean' },
    { code: 'Kannada', name: 'Kannada' },
    { code: 'Telugu', name: 'Telugu' },
    { code: 'Tamil', name: 'Tamil' },
    { code: 'Bengali', name: 'Bengali' },
    { code: 'Marathi', name: 'Marathi' },
    { code: 'Arabic', name: 'Arabic' },
    { code: 'Malayalam', name: 'Malayalam' },
    { code: 'Italian', name: 'Italian' },
    { code: 'Dutch', name: 'Dutch' },
    { code: 'Polish', name: 'Polish' },
    { code: 'Turkish', name: 'Turkish' },
    { code: 'Greek', name: 'Greek' },
    { code: 'Japanese', name: 'Japanese' },
    { code: 'Thai', name: 'Thai' },
    { code: 'Vietnamese', name: 'Vietnamese' },
    { code: 'Indonesian', name: 'Indonesian' },
    { code: 'Malay', name: 'Malay' },
    { code: 'Burmese', name: 'Burmese' },
    { code: 'Persian', name: 'Persian' },
    { code: 'Hebrew', name: 'Hebrew' },
    { code: 'Swahili', name: 'Swahili' },
    { code: 'Hausa', name: 'Hausa' },
    { code: 'Urdu', name: 'Urdu' },
    { code: 'Punjabi', name: 'Punjabi' },
    { code: 'Sinhala', name: 'Sinhala' },
    { code: 'Nepali', name: 'Nepali' },
    { code: 'Tagalog', name: 'Tagalog' },
    { code: 'Khmer', name: 'Khmer' },
    { code: 'Lao', name: 'Lao' },
    { code: 'Swedish', name: 'Swedish' },
    { code: 'Norwegian', name: 'Norwegian' },
    { code: 'Danish', name: 'Danish' },
    { code: 'Finnish', name: 'Finnish' },
    { code: 'Hungarian', name: 'Hungarian' },
    { code: 'Czech', name: 'Czech' },
    { code: 'Romanian', name: 'Romanian' },
    { code: 'Yoruba', name: 'Yoruba' },
    { code: 'Igbo', name: 'Igbo' },
    { code: 'Amharic', name: 'Amharic' },
    { code: 'Somali', name: 'Somali' },
    { code: 'Xhosa', name: 'Xhosa' },
    { code: 'Zulu', name: 'Zulu' },
    { code: 'Quechua', name: 'Quechua' },
    { code: 'Catalan', name: 'Catalan' },
    { code: 'Uzbek', name: 'Uzbek' },
    { code: 'Kazakh', name: 'Kazakh' },
    { code: 'Mongolian', name: 'Mongolian' },
    { code: 'Tibetan', name: 'Tibetan' },
    { code: 'Armenian', name: 'Armenian' },
    { code: 'Georgian', name: 'Georgian' },
];

const PANEL_ORIENTATIONS = {
    HORIZONTAL: 'horizontal',
    VERTICAL: 'vertical',
};


// Maximum number of history states to keep for undo functionality
const MAX_HISTORY_SIZE = AppConfig.historySize;

const PROCESS_STAGES = [
    'LEVEL1_WIP',
    'LEVEL1_SEVAK_COMPLETE',
    'LEVEL2_WIP',
    'LEVEL2_SEVAK_COMPLETE',
    'LEVEL3_WIP',
    'LEVEL3_SEVAK_COMPLETE',
    'PUBLISHED',
];

const SELECTABLE_PROCESS_STAGES = PROCESS_STAGES.filter((stage) => !stage.endsWith('_WIP'));

const hasHtmlMarkup = (value) => /<\/?[a-z][\s\S]*>/i.test(value || '');

const escapeHtml = (value = '') =>
    value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

const sanitizeHtmlContent = (html) => {
    if (!html) return html;
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Extract styles, scope body/html selectors to a wrapper class so they
    // don't bleed into the app layout, but preserve all class-based formatting.
    let scopedCss = '';
    doc.querySelectorAll('style').forEach((style) => {
        scopedCss += style.textContent
            .replace(/\bbody\b/g, '.te-doc-root')
            .replace(/\bhtml\b/g, '.te-doc-root');
        style.remove();
    });
    doc.querySelectorAll('script, link[rel="stylesheet"]').forEach((el) => el.remove());

    // Unwrap existing te-doc-root divs to prevent recursive nesting shown in payload
    doc.querySelectorAll('.te-doc-root').forEach((root) => {
        const parent = root.parentNode;
        if (parent) {
            while (root.firstChild) parent.insertBefore(root.firstChild, root);
            parent.removeChild(root);
        }
    });

    const bodyHtml = doc.body ? doc.body.innerHTML : html;

    if (scopedCss) {
        return `<style>${scopedCss}</style><div class="te-doc-root">${bodyHtml}</div>`;
    }
    return bodyHtml;
};

/**
 * Re-applies highlight spans into HTML for highlights whose spans are missing from the stored blob.
 * Called on load when MongoDB has highlights but the blob HTML doesn't have the spans.
 */
const applyHighlightsToHtml = (html, highlights) => {
    if (!highlights || !highlights.length || !html) return html;
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    highlights.forEach(h => {
        if (!h.highlightedText || doc.querySelector(`[data-highlight-id="${h.id}"]`)) return;
        const walker = document.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, null, false);
        let textNode;
        while ((textNode = walker.nextNode())) {
            // Skip text already inside a highlight span
            if (textNode.parentElement?.closest('.text-highlight')) continue;
            const idx = textNode.textContent.indexOf(h.highlightedText);
            if (idx !== -1) {
                const range = doc.createRange();
                range.setStart(textNode, idx);
                range.setEnd(textNode, idx + h.highlightedText.length);
                const span = doc.createElement('span');
                span.className = 'text-highlight';
                span.dataset.highlightId = h.id;
                span.style.backgroundColor = 'rgba(255, 235, 59, 0.4)';
                span.style.cursor = 'pointer';
                if (h.comment) span.title = h.comment;
                try { range.surroundContents(span); } catch (e) {}
                break;
            }
        }
    });

    return doc.body.innerHTML;
};

/**
 * Extracts highlight metadata from HTML span tags as a recovery mechanism
 */
const extractHighlightsFromHtml = (html) => {
    if (!html) return [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const highlightsMap = new Map();

    doc.querySelectorAll('.text-highlight').forEach((span) => {
        const id = span.getAttribute('data-highlight-id') || uuidv4();
        if (!highlightsMap.has(id)) {
            highlightsMap.set(id, {
                id: id,
                highlightedText: span.textContent,
                comment: span.getAttribute('title') || '',
                createdAt: new Date().toISOString()
            });
        }
    });
    return Array.from(highlightsMap.values());
};

const toEditorHtml = (value = '') => {
    if (!value) return '';
    if (hasHtmlMarkup(value)) return sanitizeHtmlContent(value);
    return escapeHtml(value).replace(/\n/g, '<br/>');
};

const FormattingToolbar = ({ editorRef, onChange, disabled, darkMode }) => {
    const exec = (command, commandValue = null) => {
        if (disabled || !editorRef.current) return;
        editorRef.current.focus();
        document.execCommand(command, false, commandValue);
        onChange(editorRef.current.innerHTML);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
                p: 0.75,
                borderBottom: '1px solid',
                borderColor: darkMode ? '#555555' : '#e0e0e0',
                bgcolor: darkMode ? '#2a2a2a' : '#fafafa',
                borderRadius: '4px 4px 0 0',
                border: '1px solid',
                borderBottomWidth: 0,
            }}
        >
            <Tooltip title="Bold">
                <span><IconButton size="small" onClick={() => exec('bold')} disabled={disabled}><FormatBoldIcon fontSize="small" /></IconButton></span>
            </Tooltip>
            <Tooltip title="Italic">
                <span><IconButton size="small" onClick={() => exec('italic')} disabled={disabled}><FormatItalicIcon fontSize="small" /></IconButton></span>
            </Tooltip>
            <Tooltip title="Underline">
                <span><IconButton size="small" onClick={() => exec('underline')} disabled={disabled}><FormatUnderlinedIcon fontSize="small" /></IconButton></span>
            </Tooltip>
            <Tooltip title="Bulleted List">
                <span><IconButton size="small" onClick={() => exec('insertUnorderedList')} disabled={disabled}><FormatListBulletedIcon fontSize="small" /></IconButton></span>
            </Tooltip>
            <Tooltip title="Numbered List">
                <span><IconButton size="small" onClick={() => exec('insertOrderedList')} disabled={disabled}><FormatListNumberedIcon fontSize="small" /></IconButton></span>
            </Tooltip>
            <Tooltip title="Insert Link">
                <span>
                    <IconButton
                        size="small"
                        disabled={disabled}
                        onClick={() => {
                            const url = window.prompt('Enter link URL');
                            if (url) exec('createLink', url);
                        }}
                    >
                        <InsertLinkIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
            <Tooltip title="Clear Formatting">
                <span><IconButton size="small" onClick={() => exec('removeFormat')} disabled={disabled}><FormatClearIcon fontSize="small" /></IconButton></span>
            </Tooltip>
        </Box>
    );
};

const RichHtmlEditor = forwardRef(({ value, onChange, readOnly, darkMode, height, compact, fontSize = 14, onHighlightClick, onMouseUp }, ref) => {
    const editorRef = useRef(null);

    useImperativeHandle(ref, () => ({ editorRef, onChange }));

    // Caret-preserving sync -- see useCaretPreservingContentEditable.js (also reused by
    // IdmlPageSpreadEditor.js's contentEditable target frames, design doc §7).
    useCaretPreservingHtmlSync(editorRef, value);

    return (
        <Box
            ref={editorRef}
            contentEditable={!readOnly}
            suppressContentEditableWarning={true}
            onInput={(event) => handleCaretPreservingInput(event, onChange)}
            onMouseUp={onMouseUp}
            onClick={(e) => {
                const highlight = e.target.closest('.text-highlight');
                if (highlight && onHighlightClick) {
                    onHighlightClick(highlight.dataset.highlightId);
                }
            }}
            sx={{
                height,
                overflowY: 'auto',
                ...(compact ? { px: 2, py: 0 } : { p: 2 }),
                bgcolor: readOnly ? (darkMode ? '#252525' : '#f8f8f8') : darkMode ? '#1e1e1e' : '#ffffff',
                color: darkMode ? '#ffffff' : '#111111',
                outline: 'none',
                lineHeight: 1.6,
                fontSize: `${fontSize}px`,
                // FORCE ALL INLINE ELEMENTS AND CHILD NODES TO SCALE VISUALLY
                '& *': {
                    fontSize: `${fontSize}px !important`,
                },
                '& .text-highlight': {
                    backgroundColor: 'rgba(255, 235, 59, 0.4) !important',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    '&:hover': { backgroundColor: 'rgba(255, 235, 59, 0.7) !important' }
                },
                '&:focus': {
                    boxShadow: readOnly ? 'none' : `inset 0 0 0 2px ${darkMode ? '#90caf9' : '#1976d2'}`,
                },
                '& .te-doc-root': {
                    width: '100%',
                    maxWidth: '100% !important',
                    boxSizing: 'border-box',
                    margin: '0 !important',
                    fontSize: 'inherit',
                    ...(compact && { padding: '8px !important' }),
                },
            }}
        />
    );
});

const TextEditor = ({ darkMode, toggleDarkMode, themeVariant }) => {
    useTrackUsage('TextEditor', 'editor', 'textfiles');
    const { id } = useParams();
    const navigate = useNavigate();

    const [textFile, setTextFile] = useState(null);
    const [content, setContent] = useState('');
    const [originalContent, setOriginalContent] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [rightActiveTab, setRightActiveTab] = useState(0);
    const [leftActiveTab, setLeftActiveTab] = useState(0);
    const [translationLanguage, setTranslationLanguage] = useState('');
    const [translationContent, setTranslationContent] = useState('');
    const [availableTranslations, setAvailableTranslations] = useState([]);
    const requestedLangRef = useRef(new URLSearchParams(window.location.search).get('lang'));
    const [showTranslationDialog, setShowTranslationDialog] = useState(false);
    const [isTranslating, setIsTranslating] = useState(false);
    const [translateDialogTab, setTranslateDialogTab] = useState(0);
    const [uploadTranslationFile, setUploadTranslationFile] = useState(null);
    const [isUploadingTranslation, setIsUploadingTranslation] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [breadcrumbs, setBreadcrumbs] = useState([]);
    const [downloadMenuAnchorEl, setDownloadMenuAnchorEl] = useState(null);
    const [breadcrumb, setBreadcrumb] = useState([]);
    const [sourceLanguage, setSourceLanguage] = useState('');
    const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
    const [showChangeLevelDialog, setShowChangeLevelDialog] = useState(false);
    const [savingStage, setSavingStage] = useState(false);
    const [selectedLevelStage, setSelectedLevelStage] = useState('');
    const [sourceFontSize, setSourceFontSize] = useState(14);
    const [targetFontSize, setTargetFontSize] = useState(14);
    
    // Commenting State
    const [highlights, setHighlights] = useState([]);
    const [showCommentDialog, setShowCommentDialog] = useState(false);
    const [commentText, setCommentText] = useState('');
    const [activeHighlightId, setActiveHighlightId] = useState(null);
    const [showCommentsSidebar, setShowCommentsSidebar] = useState(false);
    const [selectionAnchor, setSelectionAnchor] = useState(null);

    const [showFormattingToolbar, setShowFormattingToolbar] = useState(false);
    const savedRangeRef = useRef(null);

    const [refreshInterval, setRefreshInterval] = useState(null);
    const [pendingTranslations, setPendingTranslations] = useState({});

    // Add this line to create the currentPath from breadcrumb
    const currentPath = breadcrumb && breadcrumb.length > 0 ? `/${breadcrumb.join('/')}` : '/';

    // Side-by-side view content
    const [leftContent, setLeftContent] = useState('');
    const [rightContent, setRightContent] = useState('');
    const [rightContentUnavailable, setRightContentUnavailable] = useState(false);

    // Refs for both panels (right is also used by the shared formatting toolbar)
    const leftEditorRef = useRef(null);
    const rightEditorRef = useRef(null);

    // Scroll synchronization toggle
    const [syncScroll, setSyncScroll] = useState(false);

    // Layout direction: 'horizontal' = left/right, 'vertical' = up/down
    const [splitDirection, setSplitDirection] = useState(PANEL_ORIENTATIONS.VERTICAL);

    // Undo history for right content
    const [rightContentHistory, setRightContentHistory] = useState([]);
    const [historyPosition, setHistoryPosition] = useState(-1);
    const isUndoAvailable = historyPosition > 0;
    // Edit session state
    const [sessionReadOnly, setSessionReadOnly] = useState(false);
    const [sessionActiveEditor, setSessionActiveEditor] = useState(null);
    const keepAliveIntervalRef = useRef(null);
    const currentSessionLanguageRef = useRef(null);

    // Inactivity tracking
    const INACTIVITY_TIMEOUT = AppConfig.sessionInactivityTimeoutMinutes * 60 * 1000;
    const [lastActivity, setLastActivity] = useState(Date.now());
    const inactivityTimeoutRef = useRef(null);

    const handleUserActivity = useCallback(
        debounce(() => { setLastActivity(Date.now()); }, 500),
        [],
    );

    const saveCurrentTranslation = async (saveType, contentOverride, highlightsOverride) => {
        const contentToSave = contentOverride !== undefined ? contentOverride : rightContent;
        const highlightsToSave = highlightsOverride !== undefined ? highlightsOverride : highlights;

        if (contentToSave === originalContent) {
            return true;
        }

        const currentLanguageCode = availableTranslations[rightActiveTab]?.code || textFile?.sourceLanguage || 'en';
        const url =
            textFile?.translations && textFile.translations[currentLanguageCode]
                ? textFile.translations[currentLanguageCode].translatedTextUrl
                : null;

        if (url === null) {
            console.log(`Cannot ${saveType?.toLowerCase() || 'save'}: URL is null`);
            return false;
        }

        await textFileServiceInstance.updateTextFileContent(id, currentLanguageCode, contentToSave, url, saveType, highlightsToSave);
        setOriginalContent(contentToSave);
        return true;
    };

    // Determine if the current user can edit the active right-panel language
    const currentRightLanguage = availableTranslations[rightActiveTab]?.code;
    const userCanEdit = currentRightLanguage
        ? isAdmin() || hasWriteAccess(currentRightLanguage)
        : false;

    // Create saveFunction for auto-save
    const saveFunction = async () => {
        try {
            return await saveCurrentTranslation('AUTO');
        } catch (error) {
            console.error('Auto-save failed:', error);
            throw error;
        }
    };

    // Use the autoSave hook
    const {
        lastSaveTime,
        isSaving: isAutoSaving,
        lastError: autoSaveError,
        saveNow,
    } = useAutoSave({
        saveFunction,
        data: { rightContent, highlights },
        enabled: autoSaveEnabled && rightContent !== originalContent && userCanEdit && !sessionReadOnly,
        saveOnUnmount: true,
        interval: 60000, // 1 minute
        inactivityDelay: 30000, // 30 seconds
    });

    const startSessionForLanguage = async (languageCode) => {
        if (!languageCode) return;
        const { email, auth } = getAuthHeaders();
        if (!email || !auth) return;
        try {
            const result = await EditSessionService.startTextFileEditSession(id, languageCode, email, auth);
            if (result.status === 'LOCKED' || result.status === 'DUPLICATE_SESSION') {
                setSessionReadOnly(true);
                setSessionActiveEditor(result.activeEditor);
            } else {
                setSessionReadOnly(false);
                setSessionActiveEditor(null);
            }
            currentSessionLanguageRef.current = languageCode;

            if (keepAliveIntervalRef.current) clearInterval(keepAliveIntervalRef.current);
            keepAliveIntervalRef.current = setInterval(async () => {
                try {
                    await EditSessionService.keepAliveTextFileEditSession(id, languageCode, email, auth);
                } catch (e) {
                    console.warn('Keep-alive failed:', e);
                }
            }, 5 * 60 * 1000); // every 5 minutes
        } catch (e) {
            console.warn('Could not start text file edit session:', e);
        }
    };

    const endCurrentSession = async () => {
        const langCode = currentSessionLanguageRef.current;
        if (!langCode) return;
        currentSessionLanguageRef.current = null;
        if (keepAliveIntervalRef.current) {
            clearInterval(keepAliveIntervalRef.current);
            keepAliveIntervalRef.current = null;
        }
        const { email, auth } = getAuthHeaders();
        if (email && auth) {
            try {
                await EditSessionService.endTextFileEditSession(id, langCode, email, auth);
            } catch (e) {
                console.warn('Could not end text file edit session:', e);
            }
        }
    };

    useEffect(() => {
        loadTextFile();

        const handleUnload = () => {
            const langCode = currentSessionLanguageRef.current;
            if (langCode) EditSessionService.endTextFileEditSessionWithBeacon(id, langCode);
        };
        window.addEventListener('beforeunload', handleUnload);

        return () => {
            window.removeEventListener('beforeunload', handleUnload);
            if (refreshInterval) clearInterval(refreshInterval);
            if (keepAliveIntervalRef.current) clearInterval(keepAliveIntervalRef.current);
            endCurrentSession();
        };
    }, [id]);

    useEffect(() => {
        if (textFile && textFile.translations) {
            const translations = Object.keys(textFile.translations).map((langName) => {
                return {
                    code: langName,
                    name: langName,
                    status: textFile.translations[langName].translationStatus || 'COMPLETED',
                };
            });
            setAvailableTranslations(translations);

            // Check for pending translations and set up auto-refresh if needed
            const inProgressTranslations = {};
            let hasPendingTranslations = false;

            translations.forEach((translation) => {
                if (translation.status === 'IN_PROGRESS' || translation.status === 'PENDING') {
                    inProgressTranslations[translation.status] = translation.status;
                    hasPendingTranslations = true;
                }
            });

            setPendingTranslations(inProgressTranslations);

            // Set up polling if there are pending translations
            if (hasPendingTranslations && !refreshInterval) {
                const interval = setInterval(() => {
                    loadTextFile();
                }, 30000); // Check every 30 seconds
                setRefreshInterval(interval);
            } else if (!hasPendingTranslations && refreshInterval) {
                clearInterval(refreshInterval);
                setRefreshInterval(null);
            }

            // Create breadcrumbs from file path
            if (textFile.path) {
                const pathParts = textFile.path.split('/').filter((part) => part.length > 0);
                const breadcrumbItems = [{ name: 'Home', path: '/' }];

                let currentPath = '';
                pathParts.forEach((part, index) => {
                    currentPath += '/' + part;
                    breadcrumbItems.push({
                        name: part,
                        path: currentPath,
                    });
                });

                setBreadcrumbs(breadcrumbItems);
            }

            // Initialize both panes with content
            // Left pane: source language; Right pane: first target (non-source) language
            if (translations.length > 0) {
                const sourceLang = textFile.sourceLanguage || 'English';
                const sourceIdx = translations.findIndex((t) => t.code === sourceLang);
                const leftIdx = sourceIdx >= 0 ? sourceIdx : 0;

                setLeftActiveTab(leftIdx);
                loadPaneContent(translations[leftIdx].code, 'left');

                // Opened from a task (?lang=): show that language first, once
                const wantedIdx = translations.findIndex((t, i) => i !== leftIdx && t.code === requestedLangRef.current);
                requestedLangRef.current = null;
                const rightIdx = wantedIdx >= 0 ? wantedIdx : translations.findIndex((_, i) => i !== leftIdx);
                const rightLang = rightIdx >= 0 ? translations[rightIdx].code : translations[leftIdx].code;
                if (rightIdx >= 0) {
                    setRightActiveTab(rightIdx);
                    loadPaneContent(translations[rightIdx].code, 'right');
                } else {
                    loadPaneContent(translations[leftIdx].code, 'right');
                }
                // Only start a session if we don't already have one active for this language.
                // useEffect([textFile]) fires on every reload/poll; without this guard the
                // session would be re-requested repeatedly, which can reset sessionReadOnly.
                if (!currentSessionLanguageRef.current) {
                    startSessionForLanguage(rightLang);
                }
            }
        }
    }, [textFile]);

    // Scroll synchronization between source and translated panels
    useEffect(() => {
        if (!syncScroll) return;

        const leftEl = leftEditorRef.current?.editorRef?.current;
        const rightEl = rightEditorRef.current?.editorRef?.current;
        if (!leftEl || !rightEl) return;

        let isSyncingLeft = false;
        let isSyncingRight = false;

        const handleLeftScroll = () => {
            if (isSyncingLeft) return;
            isSyncingRight = true;
            const maxLeft = leftEl.scrollHeight - leftEl.clientHeight;
            const ratio = maxLeft > 0 ? leftEl.scrollTop / maxLeft : 0;
            rightEl.scrollTop = ratio * (rightEl.scrollHeight - rightEl.clientHeight);
            requestAnimationFrame(() => { isSyncingRight = false; });
        };

        const handleRightScroll = () => {
            if (isSyncingRight) return;
            isSyncingLeft = true;
            const maxRight = rightEl.scrollHeight - rightEl.clientHeight;
            const ratio = maxRight > 0 ? rightEl.scrollTop / maxRight : 0;
            leftEl.scrollTop = ratio * (leftEl.scrollHeight - leftEl.clientHeight);
            requestAnimationFrame(() => { isSyncingLeft = false; });
        };

        leftEl.addEventListener('scroll', handleLeftScroll);
        rightEl.addEventListener('scroll', handleRightScroll);

        return () => {
            leftEl.removeEventListener('scroll', handleLeftScroll);
            rightEl.removeEventListener('scroll', handleRightScroll);
        };
    }, [syncScroll, isLoading]);

    // Inactivity timeout — end session and navigate home after 15 min with no activity
    useEffect(() => {
        const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click', 'keydown'];
        activityEvents.forEach((event) => document.addEventListener(event, handleUserActivity));

        const checkInactivity = () => {
            const timeSinceLastActivity = Date.now() - lastActivity;
            if (timeSinceLastActivity >= INACTIVITY_TIMEOUT) {
                endCurrentSession();
                Swal.fire({
                    title: 'Session Expired',
                    text: 'You have been inactive for 15 minutes. Your session has been closed.',
                    icon: 'info',
                    timer: 5000,
                    timerProgressBar: true,
                }).then(() => navigate('/'));
                if (inactivityTimeoutRef.current) clearInterval(inactivityTimeoutRef.current);
            }
        };
        inactivityTimeoutRef.current = setInterval(checkInactivity, 60000);

        return () => {
            activityEvents.forEach((event) => document.removeEventListener(event, handleUserActivity));
            if (inactivityTimeoutRef.current) clearInterval(inactivityTimeoutRef.current);
        };
    }, [lastActivity, handleUserActivity, navigate]);

    // Display auto-save error if one occurs
    useEffect(() => {
        if (autoSaveError) {
            setError(`Auto-save error: ${autoSaveError.message || 'Unknown error'}`);

            // Clear error after 5 seconds
            const timer = setTimeout(() => {
                setError(null);
            }, 5000);

            return () => clearTimeout(timer);
        }
    }, [autoSaveError]);

    // Add this to your component's useEffect section
    useEffect(() => {
        // Add keyframes animation to document if not already present
        if (!document.getElementById('autosave-pulse-animation')) {
            const style = document.createElement('style');
            style.id = 'autosave-pulse-animation';
            style.textContent = `
                @keyframes pulse {
                    0% { opacity: 0.6; }
                    50% { opacity: 1; }
                    100% { opacity: 0.6; }
                }
            `;
            document.head.appendChild(style);

            return () => {
                const element = document.getElementById('autosave-pulse-animation');
                if (element) document.head.removeChild(element);
            };
        }
    }, []);

    const loadTextFile = async () => {
        setIsLoading(true);
        setError(null);

        const pagebreadcrumbs = JSON.parse(localStorage.getItem('breadcrumbs'));
        setBreadcrumb(pagebreadcrumbs);

        try {
            const fileData = await textFileServiceInstance.getTextFileById(id);
            setTextFile(fileData);

            // Get the source languages and then get translations for that language
            const sourceLang = fileData.sourceLanguage || 'English';
            if (fileData.translations && fileData.translations[sourceLang]) {
                const translatedTextUrl = fileData.translations[sourceLang].translatedTextUrl;
                if (translatedTextUrl) {
                    const contentData = await textFileServiceInstance.getTextFileContent(translatedTextUrl);
                    const editorContent = toEditorHtml(contentData || '');
                    setContent(editorContent);
                    setOriginalContent(editorContent);
                } else {
                    // If no URL is available, fallback to empty content
                    setContent('');
                    setOriginalContent('');
                }
            } else {
                // If no translations for source language, load original content
                const contentData = await textFileServiceInstance.getTextFileContent(fileData.url);
                const editorContent = toEditorHtml(contentData || '');
                setContent(editorContent);
                setOriginalContent(editorContent);
            }
        } catch (err) {
            console.error('Error loading text file:', err);
            setError('Failed to load text file: ' + (err.response?.data?.error || err.message));
        } finally {
            setIsLoading(false);
        }
    };

    // Modify the loadPaneContent function to properly handle content comparisons
    const loadPaneContent = async (languageCode, pane) => {
        try {
            if (textFile && textFile.translations && textFile.translations[languageCode]) {
                // Check if translation is still in progress
                const translationStatus = textFile.translations[languageCode].translationStatus;
                if (translationStatus === 'IN_PROGRESS' || translationStatus === 'PENDING') {
                    const pendingMessage = `Translation to ${languageCode} is in progress. Please wait...`;
                    if (pane === 'left') {
                        setLeftContent(pendingMessage);
                    } else {
                        // Use this temporary flag to prevent content change detection
                        const isLoading = true;

                        setRightContent(pendingMessage);
                        setContent(pendingMessage);
                        setOriginalContent(pendingMessage);

                        // Initialize history with the loaded content
                        setRightContentHistory([pendingMessage]);
                        setHistoryPosition(0);
                    }
                    return;
                }

                const translatedTextUrl = textFile.translations[languageCode].translatedTextUrl;

                if (!translatedTextUrl && pane === 'right') {
                    setRightContentUnavailable(true);
                    setRightContent('');
                    setContent('');
                    setOriginalContent('');
                    setRightContentHistory(['']);
                    setHistoryPosition(0);
                    return;
                }

                setRightContentUnavailable(false);
                const translatedContent = translatedTextUrl
                    ? await textFileServiceInstance.getTextFileContent(translatedTextUrl)
                    : null;

                if (pane === 'left') {
                    setLeftContent(toEditorHtml(translatedContent || ''));
                } else {
                    const rawEditorContent = toEditorHtml(translatedContent || '');

                    // Restore highlights state: prefer API metadata, fallback to HTML attributes
                    const backendHighlights = textFile.translations[languageCode].highlights || [];
                    const htmlHighlights = extractHighlightsFromHtml(rawEditorContent);

                    let editorContent = rawEditorContent;
                    let restoredHighlights;

                    if (backendHighlights.length > 0) {
                        // Re-apply any spans that are missing from the stored blob HTML
                        if (htmlHighlights.length < backendHighlights.length) {
                            editorContent = applyHighlightsToHtml(rawEditorContent, backendHighlights);
                        }
                        // For highlights saved before the ObjectMapper fix, comment may be null/empty in
                        // MongoDB but still present as the span's title attribute in the blob HTML.
                        // Merge: prefer backendHighlight data but recover comment from HTML span if missing.
                        const htmlHighlightMap = {};
                        htmlHighlights.forEach(h => { htmlHighlightMap[h.id] = h; });
                        restoredHighlights = backendHighlights.map(h =>
                            (!h.comment && htmlHighlightMap[h.id]?.comment)
                                ? { ...h, comment: htmlHighlightMap[h.id].comment }
                                : h
                        );
                    } else if (htmlHighlights.length > 0) {
                        // Recover comments from Blob storage if database metadata is missing
                        restoredHighlights = htmlHighlights;
                    } else {
                        restoredHighlights = [];
                    }

                    // First set the original content to avoid comparing against old content
                    setOriginalContent(editorContent);

                    // Then update displayed content and the content for saving
                    setRightContent(editorContent);
                    setContent(editorContent);

                    // Initialize history with the loaded content, but only one entry
                    setRightContentHistory([editorContent]);
                    setHistoryPosition(0);

                    setHighlights(restoredHighlights);
                }
            } else {
                throw new Error(`Translation for ${languageCode} not found`);
            }
        } catch (err) {
            console.error(`Error loading ${pane} pane content:`, err);
            setError(`Failed to load ${pane} pane content: ` + (err.response?.data?.error || err.message));
        }
    };

    // Function to add a new state to history
    const addToHistory = (newContent) => {
        // Only add to history if content is different from the most recent entry
        if (rightContentHistory.length === 0 || newContent !== rightContentHistory[historyPosition]) {
            // Create a new array with previous history up to current position + new state
            const newHistory = [...rightContentHistory.slice(0, historyPosition + 1), newContent];

            // Limit history size by trimming older entries if needed
            const trimmedHistory =
                newHistory.length > MAX_HISTORY_SIZE
                    ? newHistory.slice(newHistory.length - MAX_HISTORY_SIZE)
                    : newHistory;

            setRightContentHistory(trimmedHistory);
            setHistoryPosition(trimmedHistory.length - 1);
        }
    };

    // Handle undo functionality
    const handleUndo = () => {
        if (historyPosition > 0) {
            const newPosition = historyPosition - 1;
            setHistoryPosition(newPosition);
            setRightContent(rightContentHistory[newPosition]);
        }
    };

    const handleSelection = useCallback(() => {
        // If the comment dialog is open, hide the FAB to prevent it from floating over or near the dialog
        if (showCommentDialog) {
            setSelectionAnchor(null);
            return;
        }

        const selection = window.getSelection();
        if (selection.rangeCount > 0 && !selection.isCollapsed) {
            const range = selection.getRangeAt(0);
            
            // Only show if selection is within the right editor
            const container = rightEditorRef.current?.editorRef?.current;
            if (container && !container.contains(range.commonAncestorContainer)) {
                setSelectionAnchor(null);
                return;
            }

            const rect = range.getBoundingClientRect();
            savedRangeRef.current = range.cloneRange();
            setSelectionAnchor({
                top: rect.top - 40,
                left: rect.left + (rect.width / 2),
            });
        } else {
            setSelectionAnchor(null);
        }
    }, [showCommentDialog]);

    useEffect(() => {
        document.addEventListener('selectionchange', handleSelection);
        return () => document.removeEventListener('selectionchange', handleSelection);
    }, [handleSelection]);

    /**
     * Robustly wrap a range with a highlight span. 
     * Iterates through all text nodes in the range to avoid breaking complex/overlapping HTML structures.
     */
    const wrapRangeWithHighlight = (range, highlightId) => {
        // TreeWalker must root at an Element, not a Text node.
        // When a selection is entirely within one text node, commonAncestorContainer IS that
        // text node — use its parent so the walker can traverse into it.
        const root = range.commonAncestorContainer.nodeType === Node.TEXT_NODE
            ? range.commonAncestorContainer.parentNode
            : range.commonAncestorContainer;

        const walker = document.createTreeWalker(
            root,
            NodeFilter.SHOW_TEXT,
            null,
            false
        );

        const nodesToWrap = [];
        let node = walker.nextNode();
        while (node) {
            if (range.intersectsNode(node)) {
                nodesToWrap.push(node);
            }
            node = walker.nextNode();
        }

        nodesToWrap.forEach(textNode => {
            const nodeRange = document.createRange();
            nodeRange.selectNodeContents(textNode);

            if (textNode === range.startContainer) {
                nodeRange.setStart(textNode, range.startOffset);
            }
            if (textNode === range.endContainer) {
                nodeRange.setEnd(textNode, range.endOffset);
            }

            if (!nodeRange.collapsed && nodeRange.toString().trim().length > 0) {
                const span = document.createElement('span');
                span.className = 'text-highlight';
                span.dataset.highlightId = highlightId;
                span.style.backgroundColor = 'rgba(255, 235, 59, 0.4)';
                span.style.cursor = 'pointer';
                try {
                    nodeRange.surroundContents(span);
                } catch (e) {
                    // Fallback for edge cases where surroundContents still fails on a text node sub-range
                    const fragment = nodeRange.extractContents();
                    span.appendChild(fragment);
                    nodeRange.insertNode(span);
                }
            }
        });
    };

    const handleAddHighlight = () => {
        const selection = window.getSelection();
        let range;
        if (!selection.rangeCount || selection.isCollapsed) {
            // Clicking the button clears the browser selection — use the range saved on selectionchange
            if (!savedRangeRef.current) {
                Swal.fire('No selection', 'Please select some text to comment on.', 'info');
                return;
            }
            range = savedRangeRef.current;
        } else {
            range = selection.getRangeAt(0).cloneRange();
        }
        savedRangeRef.current = null;
        const text = range.toString();
        if (!text.trim()) {
            Swal.fire('No selection', 'Please select some text to comment on.', 'info');
            return;
        }
        
        try {
            const highlightId = uuidv4();
            wrapRangeWithHighlight(range, highlightId);

            setSelectionAnchor(null);
            selection.removeAllRanges();

            const newHighlight = {
                id: highlightId,
                highlightedText: text,
                comment: '',
                createdBy: getAuthHeaders().email,
                createdAt: new Date().toISOString()
            };
            setHighlights(prev => [...prev, newHighlight]);
            setActiveHighlightId(highlightId);
            setCommentText('');
            setShowCommentDialog(true);
            handleContentChange(rightEditorRef.current.editorRef.current.innerHTML);
        } catch (e) {
            Swal.fire('Selection Error', 'Cannot highlight overlapping or complex HTML structures.', 'error');
        }
    };

    const saveComment = async () => {
        const updatedHighlights = highlights.map(h =>
            h.id === activeHighlightId ? { ...h, comment: commentText } : h
        );
        setHighlights(updatedHighlights);

        // Update the DOM/HTML to include the title attribute for tooltips and persistence
        const editor = rightEditorRef.current.editorRef.current;
        const spans = editor.querySelectorAll(`span[data-highlight-id="${activeHighlightId}"]`);
        spans.forEach(span => {
            span.title = commentText;
        });

        // Capture updated HTML after DOM mutation
        const updatedContent = editor.innerHTML;

        // Sync the HTML content state so it gets saved to the blob
        handleContentChange(updatedContent);

        setShowCommentDialog(false);

        // Save explicitly with fresh values to avoid stale closure sending empty comment
        await saveCurrentTranslation('MANUAL', updatedContent, updatedHighlights);
    };

    const deleteHighlight = async (hid) => {
        const editor = rightEditorRef.current.editorRef.current;
        const spans = editor.querySelectorAll(`span[data-highlight-id="${hid}"]`);
        spans.forEach(span => {
            const parent = span.parentNode;
            while (span.firstChild) parent.insertBefore(span.firstChild, span);
            parent.removeChild(span);
        });
        const updatedHighlights = highlights.filter(h => h.id !== hid);
        setHighlights(updatedHighlights);
        const updatedContent = editor.innerHTML;
        handleContentChange(updatedContent);
        await saveCurrentTranslation('MANUAL', updatedContent, updatedHighlights);
    };

    const clearAllComments = () => {
        Swal.fire({
            title: 'Clear all comments?',
            text: "This will remove all highlights and comments from this language.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, clear all'
        }).then(async (result) => {
            if (result.isConfirmed) {
                const editor = rightEditorRef.current.editorRef.current;
                const spans = editor.querySelectorAll('.text-highlight');
                spans.forEach(span => {
                    const parent = span.parentNode;
                    while (span.firstChild) parent.insertBefore(span.firstChild, span);
                    parent.removeChild(span);
                });
                const updatedContent = editor.innerHTML;
                setHighlights([]);
                handleContentChange(updatedContent);
                await saveCurrentTranslation('MANUAL', updatedContent, []);
            }
        });
    };

    const handleSave = async () => {
        if (rightContent === originalContent) {
            return; // No changes
        }

        setIsSaving(true);
        setError(null);

        try {
            await saveCurrentTranslation('MANUAL');
            setSuccessMessage('Changes saved successfully');

            // Hide success message after 3 seconds
            setTimeout(() => {
                setSuccessMessage(null);
            }, 3000);
        } catch (err) {
            console.error('Error saving text file:', err);
            setError('Failed to save changes: ' + (err.response?.data?.error || err.message));
        } finally {
            setIsSaving(false);
        }
    };

    const handleLeftTabChange = async (event, newValue) => {
        setLeftActiveTab(newValue);
        setError(null);

        // Get the language from the available translations
        const langName = availableTranslations[newValue].code;
        await loadPaneContent(langName, 'left');
    };

    // Update the handleRightTabChange function to prevent falsely detecting changes
    const handleRightTabChange = async (event, newValue) => {
        // If there are genuinely unsaved changes, confirm before switching
        if (rightContent !== originalContent && historyPosition > 0) {
            if (window.confirm('You have unsaved changes. Do you want to save before switching?')) {
                await saveCurrentTranslation('MANUAL');
            }
        }

        // End session for current language before switching
        await endCurrentSession();
        setSessionReadOnly(false);
        setSessionActiveEditor(null);

        setRightActiveTab(newValue);
        setError(null);
        setTranslationLanguage(availableTranslations[newValue].code);

        // Get the language from the available translations
        const langName = availableTranslations[newValue].code;
        await loadPaneContent(langName, 'right');
        await startSessionForLanguage(langName);
    };

    const handleOpenDownloadMenu = (event) => {
        setDownloadMenuAnchorEl(event.currentTarget);
    };

    const handleCloseDownloadMenu = () => {
        setDownloadMenuAnchorEl(null);
    };

    const handleTranslateClick = () => {
        setTranslationLanguage('');
        setSourceLanguage(textFile?.sourceLanguage || '');
        setTranslateDialogTab(0);
        setUploadTranslationFile(null);
        setShowTranslationDialog(true);
    };

    const handleGlossaryClick = () => {
        const normalizedCurrentPath = currentPath || '/';
        const rawPath = textFile?.path || normalizedCurrentPath;
        const normalizedPath = rawPath.startsWith('/') ? rawPath : `/${rawPath}`;

        // If backend path contains filename, trim it; otherwise keep full folder path.
        const fileSuffix = textFile?.name ? `/${textFile.name}` : '';
        const folderPath = fileSuffix && normalizedPath.endsWith(fileSuffix)
            ? (normalizedPath.slice(0, -fileSuffix.length) || '/')
            : normalizedPath;

        navigate(`/glossary/textfile/${id}`, { 
            state: { 
                backTo: `/textfiles/edit/${id}`,
                fromPath: folderPath 
            } 
        });
    };

    // Inside your component
    const handleBreadcrumbNavigate = ({ path }) => {
        console.log(`Navigating to breadcrumb path: ${path}`);

        // Update breadcrumbs in localStorage if needed
        const newBreadcrumbs = path.split('/').filter((segment) => segment.length > 0);
        localStorage.setItem('breadcrumbs', JSON.stringify(newBreadcrumbs));

        // Navigate with state
        navigate('/', {
            state: {
                fromVideoEdit: true,
                folderPath: path,
                refreshNeeded: true,
            },
        });
    };

    const handleCreateTranslation = async () => {
        if (!translationLanguage) {
            setError('Please select a language');
            return;
        }

        if (!sourceLanguage) {
            setError('Please select the source language');
            return;
        }

        setIsTranslating(true);
        setError(null);

        try {
            // Call translation service to translate the content
            await textFileServiceInstance.startTranslation(id, sourceLanguage, [translationLanguage]);

            // Reload the text file to get updated translations
            await loadTextFile();

            // Close the dialog
            setShowTranslationDialog(false);

            // Update available translations
            if (!availableTranslations.some((t) => t.code === translationLanguage)) {
                setAvailableTranslations([
                    ...availableTranslations,
                    { code: translationLanguage, name: translationLanguage },
                ]);
            }
        } catch (err) {
            console.error('Error creating translation:', err);
            setError('Failed to create translation: ' + (err.response?.data?.error || err.message));
        } finally {
            setIsTranslating(false);
        }
    };

    const handleUploadTranslation = async () => {
        if (!translationLanguage || !uploadTranslationFile) return;
        setIsUploadingTranslation(true);
        setError(null);
        try {
            const content = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = (e) => resolve(e.target.result);
                reader.onerror = reject;
                reader.readAsText(uploadTranslationFile);
            });
            await textFileServiceInstance.addTranslation(id, translationLanguage, content);
            await loadTextFile();
            setShowTranslationDialog(false);
            setUploadTranslationFile(null);
            if (!availableTranslations.some((t) => t.code === translationLanguage)) {
                setAvailableTranslations([
                    ...availableTranslations,
                    { code: translationLanguage, name: translationLanguage },
                ]);
            }
        } catch (err) {
            console.error('Error uploading translation:', err);
            setError('Failed to upload translation: ' + (err.response?.data?.error || err.message));
        } finally {
            setIsUploadingTranslation(false);
        }
    };

    const handleDownloadAsText = () => {
        handleCloseDownloadMenu();
        try {
            const title = textFile?.name || 'Document';
            const currentTabName = availableTranslations[rightActiveTab].name;

            // Create a blob from the content
            const blob = new Blob([htmlToPlainText(rightContent)], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);

            // Create download link
            const a = document.createElement('a');
            a.href = url;
            a.download = `${title}_${currentTabName}.txt`;
            document.body.appendChild(a);
            a.click();

            // Cleanup
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            setSuccessMessage('Text file downloaded successfully');
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err) {
            console.error('Error downloading text file:', err);
            setError('Failed to download text file: ' + err.message);
        }
    };

    const handleDownloadAsPDF = async () => {
        handleCloseDownloadMenu();
        try {
            const title = textFile?.name || 'Document';
            const currentTabName = availableTranslations[rightActiveTab].name;
            const blob = await htmlToPdfBlob(rightContent);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${title}_${currentTabName}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            setSuccessMessage('PDF downloaded successfully');
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err) {
            console.error('Error generating PDF:', err);
            setError('Failed to generate PDF: ' + err.message);
        }
    };

    const handleDownloadAsDocx = async () => {
        handleCloseDownloadMenu();
        try {
            const title = textFile?.name || 'Document';
            const currentTabName = availableTranslations[rightActiveTab].name;
            const blob = await htmlToDocxBlob(rightContent);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${title}_${currentTabName}.docx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            setSuccessMessage('DOCX downloaded successfully');
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err) {
            console.error('Error generating DOCX:', err);
            setError('Failed to generate DOCX: ' + err.message);
        }
    };


    const handleOpenLevelDialog = () => {
        const currentLanguageCode = availableTranslations[rightActiveTab]?.code;
        const currentStage = textFile?.translations?.[currentLanguageCode]?.processStage || 'LEVEL1_WIP';
        setSelectedLevelStage(currentStage);
        setShowChangeLevelDialog(true);
    };

    const handleSaveLevel = async () => {
        const currentLanguageCode = availableTranslations[rightActiveTab]?.code;
        if (!id || !currentLanguageCode || !selectedLevelStage || selectedLevelStage.endsWith('_WIP')) {
            return;
        }
        try {
            setSavingStage(true);
            await textFileServiceInstance.updateTextFileTranslationStage(id, currentLanguageCode, selectedLevelStage);
            await loadTextFile();
            setShowChangeLevelDialog(false);
        } catch (err) {
            console.error('Failed to update text file stage', err);
            setError('Failed to update level: ' + (err.response?.data?.error || err.message));
        } finally {
            setSavingStage(false);
        }
    };

    const handleGoBack = () => {
        const parentPath = textFile?.path ? textFile.path.substring(0, textFile.path.lastIndexOf('/')) : '/';
        navigate('/browse' + parentPath);
    };

    const handleBreadcrumbClick = (path) => {
        navigate('/browse' + path);
    };

    // Handle content changes with history tracking
    const handleContentChange = (value) => {
        // Skip adding to history if content matches original (typically during load)
        if (value === originalContent && historyPosition === 0) {
            setRightContent(value);
            return;
        }

        setRightContent(value);
        addToHistory(value);
    };

    // Format the lastSaveTime as a readable string
    const formatLastSaveTime = () => {
        if (!lastSaveTime) return 'Not yet saved';

        const now = new Date();
        const saveTime = new Date(lastSaveTime);
        const diffMs = now - saveTime;

        // If less than a minute ago
        if (diffMs < 60000) {
            return 'Just now';
        }

        // If less than an hour ago
        if (diffMs < 3600000) {
            const minutes = Math.floor(diffMs / 60000);
            return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
        }

        // Otherwise show the time
        return saveTime.toLocaleTimeString();
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    // Add this to determine if any translations are in progress
    const hasActiveTranslations = Object.keys(pendingTranslations).length > 0;

    // Render tab with status indicator if translation is in progress
    const renderTabLabel = (translation, isActive) => {
        const isPending = pendingTranslations[translation.status];
        const isSource = translation.status === 'SOURCE_LANGUAGE';

        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    fontWeight: isActive ? 'bold' : 'normal',
                    color: isActive ? 'secondary.main' : darkMode ? '#ffffff' : '#000000',
                }}
            >
                {translation.name}
                {isSource && (
                    <Tooltip title="Source Language - Original content that other translations are based on">
                        <TranslateIcon
                            fontSize="small"
                            color="primary"
                            sx={{
                                ml: 0.8,
                                fontSize: '1rem',
                            }}
                        />
                    </Tooltip>
                )}
                {isPending && <CircularProgress size={16} sx={{ ml: 1 }} color="secondary" />}
            </Box>
        );
    };

    return (
        <>
        <Box
            sx={{ height: '100vh', width: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
            onMouseMove={handleUserActivity}
            onClick={handleUserActivity}
            onKeyDown={handleUserActivity}
        >
            <PageHeader darkMode={darkMode} toggleDarkMode={toggleDarkMode} themeVariant={themeVariant} />
            <AppBar
                id="text-editor-appbar"
                position="static"
                sx={{
                    display: 'flex',
                    padding: 0,
                    width: '100%',
                    left: 0,
                    right: 0,
                    top: 0,
                    ml: 0,
                    mt: 0,
                    borderRadius: 0,
                }}
            >
                <Toolbar
                    sx={{
                        px: { xs: 0.5, sm: 1 },
                        minHeight: { xs: '48px' },
                        width: '100%',
                        borderRadius: 0,
                    }}
                >
                    {/* Breadcrumbs */}
                    <Box sx={{ ml: 1, flexGrow: 1 }}>
                        <ModernBreadcrumbs
                            currentPath={currentPath}
                            onNavigate={handleBreadcrumbNavigate}
                            isDarkBackground={true}
                            maxItems={5}
                        />
                        <Typography
                            color="inherit"
                            sx={{
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center',
                                mt: 0.5,
                            }}
                        >
                            <TextSnippetIcon fontSize="small" sx={{ mr: 0.5 }} />
                            {textFile?.name}
                        </Typography>
                    </Box>

                    {/* Scroll Sync Toggle */}
                    <Tooltip title={syncScroll ? 'Scroll sync ON — click to disable' : 'Sync scrolling between source and translation'}>
                        <span>
                            <IconButton
                                color="inherit"
                                onClick={() => setSyncScroll((prev) => !prev)}
                                sx={{
                                    mr: 1,
                                    bgcolor: syncScroll ? 'rgba(255,255,255,0.18)' : 'transparent',
                                    border: syncScroll ? '1px solid rgba(255,255,255,0.5)' : '1px solid transparent',
                                    borderRadius: '4px',
                                }}
                            >
                                <SyncAltIcon />
                            </IconButton>
                        </span>
                    </Tooltip>

                    {/* Layout Toggle */}
                    <Tooltip title={splitDirection === PANEL_ORIENTATIONS.HORIZONTAL ? 'Switch to top/bottom layout' : 'Switch to left/right layout'}>
                        <span>
                            <IconButton
                                color="inherit"
                                onClick={() => setSplitDirection((prev) => prev === PANEL_ORIENTATIONS.HORIZONTAL ? PANEL_ORIENTATIONS.VERTICAL : PANEL_ORIENTATIONS.HORIZONTAL)}
                                sx={{
                                    mr: 1,
                                    bgcolor: 'rgba(255,255,255,0.1)',
                                    border: '1px solid rgba(255,255,255,0.3)',
                                    borderRadius: '4px',
                                }}
                            >
                                {splitDirection === PANEL_ORIENTATIONS.HORIZONTAL ? <ViewStreamIcon /> : <ViewColumnIcon />}
                            </IconButton>
                        </span>
                    </Tooltip>

                    <Tooltip title={showFormattingToolbar ? "Hide Formatting Toolbar" : "Show Formatting Toolbar"}>
                        <span>
                            <IconButton
                                color="inherit"
                                onClick={() => setShowFormattingToolbar((prev) => !prev)}
                                sx={{
                                    mr: 1,
                                    bgcolor: !showFormattingToolbar ? 'rgba(255,255,255,0.1)' : 'transparent',
                                    borderRadius: '4px',
                                    transition: 'transform 0.2s',
                                    transform: showFormattingToolbar ? 'rotate(180deg)' : 'rotate(0deg)',
                                }}
                            >
                                <ExpandMoreIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                    
                    {/* Undo Button */}
                    <Tooltip title="Undo">
                        <span>
                            <IconButton color="inherit" onClick={handleUndo} disabled={!isUndoAvailable} sx={{ mr: 1 }}>
                                <UndoIcon />
                            </IconButton>
                        </span>
                    </Tooltip>

                    {/* Comments Toggle */}
                    <Tooltip title="View Comments">
                        <span>
                            <IconButton 
                                color="inherit" 
                                onClick={() => setShowCommentsSidebar(!showCommentsSidebar)}
                                sx={{ mr: 1, color: highlights.length > 0 ? '#ffeb3b' : 'inherit' }}
                            >
                                <ChatIcon />
                            </IconButton>
                        </span>
                    </Tooltip>

                    {/* Add Comment Button */}
                    <Tooltip title="Add Comment to Selection">
                        <span>
                            <IconButton 
                                color="inherit" 
                                onClick={handleAddHighlight}
                                disabled={sessionReadOnly || !userCanEdit}
                                sx={{ mr: 1 }}
                            >
                                <ChatBubbleOutlineIcon />
                            </IconButton>
                        </span>
                    </Tooltip>

                    {/* Download Button with Menu */}
                    <Tooltip title="Download">
                        <Button
                            color="inherit"
                            startIcon={<DownloadIcon />}
                            onClick={handleOpenDownloadMenu}
                            sx={{ mr: 1 }}
                        >
                            Download
                        </Button>
                    </Tooltip>

                    {/* Language access badge — shows EDIT or READ for the current right-panel language */}
                    {currentRightLanguage && (
                        <Tooltip
                            title={
                                userCanEdit
                                    ? `You have edit access for ${currentRightLanguage}`
                                    : `You have read-only access for ${currentRightLanguage}`
                            }
                        >
                            <Chip
                                label={userCanEdit ? 'EDIT' : 'READ'}
                                size="small"
                                sx={{
                                    mr: 1,
                                    fontWeight: 'bold',
                                    fontSize: '0.7rem',
                                    bgcolor: userCanEdit ? '#2e7d32' : '#e65100',
                                    color: '#ffffff',
                                    height: '22px',
                                }}
                            />
                        </Tooltip>
                    )}

                    <Tooltip
                        title={
                            isAutoSaving
                                ? 'Auto-saving...'
                                : lastSaveTime
                                    ? `Last saved: ${formatLastSaveTime()}`
                                    : rightContent !== originalContent
                                        ? 'Unsaved changes'
                                        : 'Not yet saved'
                        }
                    >
                        <span>
                        <Button
                            color="inherit"
                            startIcon={
                                <>
                                    <Box
                                        component="span"
                                        sx={{
                                            position: 'relative',
                                            display: 'inline-flex',
                                        }}
                                    >
                                        <SaveIcon />
                                        {rightContent !== originalContent && (
                                            <Box
                                                component="span"
                                                sx={{
                                                    position: 'absolute',
                                                    top: -2,
                                                    right: -4,
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: '50%',
                                                    backgroundColor: isAutoSaving
                                                        ? 'secondary.main' // Purple/secondary when saving
                                                        : 'error.main', // Red when unsaved
                                                    animation: isAutoSaving ? 'pulse 1.5s infinite' : 'none',
                                                }}
                                            />
                                        )}
                                    </Box>
                                </>
                            }
                            onClick={handleSave}
                            disabled={isSaving || !userCanEdit || rightContent === originalContent}
                        >
                            {isSaving ? 'Saving...' : 'Save'}
                        </Button>
                        </span>
                    </Tooltip>
                </Toolbar>
            </AppBar>

            {/* Inactivity warning — shown in the last minute before timeout */}
            {Date.now() - lastActivity > INACTIVITY_TIMEOUT - 60000 && (
                <Alert severity="warning" sx={{ borderRadius: 0, py: 0.5 }}>
                    Your session will expire in less than 1 minute due to inactivity. Move your mouse or press a key to
                    continue editing.
                </Alert>
            )}

            {/* Sticky lock banner — outside the scroll area so it always stays visible */}
            {sessionReadOnly && (
                <Alert
                    severity="warning"
                    sx={{
                        borderRadius: 0,
                        py: 0.5,
                        fontWeight: 'bold',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                    }}
                >
                    {sessionActiveEditor
                        ? `This language is currently being edited by ${sessionActiveEditor}. You are in read-only mode.`
                        : 'This language is open in another tab. You are in read-only mode.'}
                </Alert>
            )}

            <Box sx={{ px: 1, py: 1, flexGrow: 1, overflow: 'auto', width: '100%', minWidth: 0 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {successMessage && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {successMessage}
                    </Alert>
                )}

                {hasActiveTranslations && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Translation is in progress. Editing is disabled until all translations complete.
                    </Alert>
                )}

                <Grid container spacing={splitDirection === 'vertical' ? 0 : 1}>
                    {/* Top action row: Translate (left) + Glossary (right) */}
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Button
                            variant="contained"
                            color="secondary"
                            size="large"
                            startIcon={<TranslateIcon />}
                            onClick={handleTranslateClick}
                            sx={{
                                borderRadius: '8px',
                                boxShadow: 3,
                                px: 4,
                                py: 1,
                                fontSize: '1rem',
                            }}
                        >
                            Translate
                        </Button>

                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                variant="outlined"
                                color="secondary"
                                size="medium"
                                startIcon={<LayersIcon />}
                                onClick={handleOpenLevelDialog}
                                disabled={!availableTranslations[rightActiveTab]?.code}
                                sx={{
                                    borderRadius: '8px',
                                    px: 3,
                                    py: 0.75,
                                    fontSize: '0.9rem',
                                }}
                            >
                                Change Level
                            </Button>
                            <Tooltip
                                title="View & manage approved terms for this translation. Translation must be at Level 2 or above to add new terms."
                                arrow
                                placement="left"
                            >
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    size="medium"
                                    startIcon={<MenuBookIcon />}
                                    onClick={handleGlossaryClick}
                                    sx={{
                                        borderRadius: '8px',
                                        px: 3,
                                        py: 0.75,
                                        fontSize: '0.9rem',
                                    }}
                                >
                                    Term Glossary
                                </Button>
                            </Tooltip>
                        </Box>
                    </Grid>

                    {/* Formatting toolbar — horizontal mode: right-aligned above both panels */}
                    {splitDirection === PANEL_ORIENTATIONS.HORIZONTAL && (
                        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0 }}>
                            <Box id="text-editor-formatting" sx={{ width: '50%' }}>
                                <Collapse in={showFormattingToolbar} timeout="auto" unmountOnExit>
                                    <FormattingToolbar
                                        editorRef={rightEditorRef.current?.editorRef}
                                        onChange={handleContentChange}
                                        disabled={
                                            !userCanEdit ||
                                            (rightActiveTab < availableTranslations.length &&
                                            availableTranslations[rightActiveTab] &&
                                            (availableTranslations[rightActiveTab].status === 'IN_PROGRESS' ||
                                                availableTranslations[rightActiveTab].status === 'PENDING'))
                                        }
                                        darkMode={darkMode}
                                    />
                                </Collapse>
                            </Box>
                        </Grid>
                    )}

                    {/* Left panel — source language only, read-only */}
                    <Grid item xs={splitDirection === PANEL_ORIENTATIONS.HORIZONTAL ? 6 : 12}>
                        <Paper
                            elevation={3}
                            sx={{
                                borderRadius: '4px 4px 0 0',
                                overflow: 'hidden',
                            }}
                        >
                            <Box
                                sx={{
                                    bgcolor: darkMode ? '#1e1e1e' : '#e0e0e0',
                                    px: 2,
                                    py: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between', // Forces items to opposite ends of the exact same lineborderBottom: '1px solid',
                                    borderColor: darkMode ? '#555555' : '#cccccc',
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 'bold',
                                        color: darkMode ? '#ffffff' : '#000000',
                                    }}
                                >
                                    {textFile?.sourceLanguage || 'Source'} (read-only)
                                </Typography>

                                {/* Font size adjustments for Source panel */}
                                <Box 
                                    className="zoom-control-group-source"
                                    sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: 0.5, 
                                        bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: '20px',
                                        border: '1px solid',
                                        borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                                        cursor: 'default',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                                        }
                                    }}
                                >
                                    <Tooltip title="Decrease Text Size">
                                        <IconButton 
                                            size="small" 
                                            onClick={() => setSourceFontSize(prev => Math.max(12, prev - 2))}
                                            sx={{ color: darkMode ? '#aaa' : '#555', p: 0.5 }}
                                        >
                                            <ZoomOutIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    
                                    {/* Visual Support Size Badge: Changes dynamically on hover */}
                                    <Typography 
                                        variant="caption" 
                                        sx={{ 
                                            minWidth: '42px', 
                                            textAlign: 'center', 
                                            fontWeight: 600,
                                            fontSize: '0.75rem',
                                            color: darkMode ? '#fff' : '#333',
                                        }}
                                    >
                                        <Box component="span" sx={{ '.zoom-control-group-source:hover &': { display: 'none' } }}>Zoom</Box>
                                        <Box component="span" sx={{ display: 'none', '.zoom-control-group-source:hover &': { display: 'inline' } }}>{sourceFontSize}px</Box>
                                    </Typography>
                                    
                                    <Tooltip title="Increase Text Size">
                                        <IconButton 
                                            size="small" 
                                            onClick={() => setSourceFontSize(prev => Math.min(32, prev + 2))}
                                            sx={{ color: darkMode ? '#fff' : '#000', p: 0.5 }}
                                        >
                                            <ZoomInIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                        </Paper>
                        <Box
                            sx={{
                                border: '1px solid',
                                borderColor: darkMode ? '#555555' : '#cccccc',
                                borderTop: 'none',
                                borderRadius: '0 0 4px 4px',
                            }}
                        >
                            <RichHtmlEditor
                                ref={leftEditorRef}
                                value={leftContent}
                                onChange={() => {}}
                                darkMode={darkMode}
                                height={splitDirection === PANEL_ORIENTATIONS.HORIZONTAL ? 'calc(100vh - 230px)' : 'calc(50vh - 160px)'}
                                readOnly={true}
                                compact={splitDirection === PANEL_ORIENTATIONS.VERTICAL}
                                fontSize={sourceFontSize}
                            />
                        </Box>
                    </Grid>
                    {/* Formatting toolbar — vertical mode: directly above target panel */}
                    {splitDirection === PANEL_ORIENTATIONS.VERTICAL && (
                        <Grid item xs={12} sx={{ mb: 0 }}>
                            <Box id="text-editor-formatting">
                                <Collapse in={showFormattingToolbar} timeout="auto" unmountOnExit>
                                    <FormattingToolbar
                                        editorRef={rightEditorRef.current?.editorRef}
                                        onChange={handleContentChange}
                                        disabled={
                                            !userCanEdit ||
                                            (rightActiveTab < availableTranslations.length &&
                                            availableTranslations[rightActiveTab] &&
                                            (availableTranslations[rightActiveTab].status === 'IN_PROGRESS' ||
                                                availableTranslations[rightActiveTab].status === 'PENDING'))
                                        }
                                        darkMode={darkMode}
                                    />
                                </Collapse>
                            </Box>
                        </Grid>
                    )}

                    <Grid id="text-editor-content" item xs={splitDirection === PANEL_ORIENTATIONS.HORIZONTAL ? 6 : 12}>
                        <Paper
                            elevation={3}
                            sx={{
                                borderRadius: '4px 4px 0 0',
                                overflow: 'hidden',
                                bgcolor: darkMode ? '#1e1e1e' : '#e0e0e0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                pr: 2 
                            }}
                        >
                            {(() => {
                                const rightPanelTranslations = availableTranslations.filter(
                                    (t) => t.code !== textFile?.sourceLanguage
                                );
                                const rightPanelTabValue = rightPanelTranslations.findIndex(
                                    (t) => t.code === availableTranslations[rightActiveTab]?.code
                                );
                                const handleRightPanelTabChange = async (event, filteredIndex) => {
                                    const translation = rightPanelTranslations[filteredIndex];
                                    const fullIndex = availableTranslations.findIndex(
                                        (t) => t.code === translation?.code
                                    );
                                    if (fullIndex >= 0) await handleRightTabChange(event, fullIndex);
                                };
                                return (
                                    <>
                                        <Tabs
                                            id="text-editor-language-tabs"
                                            value={rightPanelTabValue < 0 ? 0 : rightPanelTabValue}
                                            onChange={handleRightPanelTabChange}
                                            indicatorColor="secondary"
                                            textColor="inherit"
                                            variant="scrollable"
                                            scrollButtons="auto"
                                            sx={{
                                                bgcolor: darkMode ? '#1e1e1e' : '#e0e0e0',
                                                flexGrow: 1, // Pushes subsequent sibling nodes cleanly to the right side
                                                '& .Mui-selected': {
                                                    fontWeight: 'bold',
                                                    color: 'secondary.main',
                                                },
                                                '& .MuiTab-root': {
                                                    fontSize: '0.9rem',
                                                    padding: '10px 16px',
                                                    backgroundColor: darkMode ? '#333333' : '#f5f5f5',
                                                    margin: '2px 2px 0 0',
                                                    borderRadius: '4px 4px 0 0',
                                                    border: darkMode ? '1px solid #555555' : '1px solid #cccccc',
                                                    borderBottom: 'none',
                                                    color: darkMode ? '#ffffff' : '#000000',
                                                },
                                                '& .Mui-selected.MuiTab-root': {
                                                    backgroundColor: darkMode ? '#4a4a4a' : '#ffffff',
                                                    boxShadow: '0 -2px 4px rgba(0,0,0,0.1)',
                                                    color: darkMode ? '#ffffff' : '#000000',
                                                },
                                            }}
                                        >
                                            {rightPanelTranslations.map((translation, index) => (
                                                <Tab
                                                    key={translation.code}
                                                    label={renderTabLabel(translation, index === rightPanelTabValue)}
                                                    sx={{
                                                        fontWeight: index === rightPanelTabValue ? 'bold' : 'normal',
                                                        '& .MuiTab-wrapper': {
                                                            color: darkMode ? '#ffffff' : '#000000',
                                                        },
                                                    }}
                                                />
                                            ))}
                                        </Tabs>
                                    </>
                                );
                            })()}
                            {/* Visual Zoom Controls for Target Pane */}
                            <Box 
                                className="zoom-control-group-target"
                                sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 0.5, 
                                    ml: 'auto', // Pushes the pill firmly to the right side of the tab action row
                                    bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: '20px',
                                    border: '1px solid',
                                    borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                                    cursor: 'default',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                                    }
                                }}
                            >
                                <Tooltip title="Decrease Text Size">
                                    <IconButton 
                                        size="small" 
                                        onClick={() => setTargetFontSize(prev => Math.max(12, prev - 2))}
                                        sx={{ color: darkMode ? '#aaa' : '#555', p: 0.5 }}
                                    >
                                        <ZoomOutIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                
                                <Typography 
                                    variant="caption" 
                                    sx={{ 
                                        minWidth: '42px', 
                                        textAlign: 'center', 
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        color: darkMode ? '#fff' : '#333',
                                    }}
                                >
                                    <Box component="span" sx={{ '.zoom-control-group-target:hover &': { display: 'none' } }}>Zoom</Box>
                                    <Box component="span" sx={{ display: 'none', '.zoom-control-group-target:hover &': { display: 'inline' } }}>{targetFontSize}px</Box>
                                </Typography>
                                
                                <Tooltip title="Increase Text Size">
                                    <IconButton 
                                        size="small" 
                                        onClick={() => setTargetFontSize(prev => Math.min(32, prev + 2))}
                                        sx={{ color: darkMode ? '#fff' : '#000', p: 0.5 }}
                                    >
                                        <ZoomInIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Paper>
                        <Box
                            sx={{
                                border: '1px solid',
                                borderColor: darkMode ? '#555555' : '#cccccc',
                                borderTop: 'none',
                                borderRadius: '0 0 4px 4px',
                                position: 'relative',
                            }}
                        >
                            {rightContentUnavailable ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: splitDirection === PANEL_ORIENTATIONS.HORIZONTAL ? 'calc(100vh - 230px)' : 'calc(50vh - 160px)',
                                        gap: 1,
                                        color: darkMode ? '#888' : '#999',
                                        backgroundColor: darkMode ? '#1e1e1e' : '#fafafa',
                                    }}
                                >
                                    <Typography variant="h6" sx={{ color: darkMode ? '#666' : '#bbb' }}>No translation available</Typography>
                                    <Typography variant="body2" sx={{ color: darkMode ? '#555' : '#ccc' }}>
                                        Use the Translate option to generate or upload a translation.
                                    </Typography>
                                </Box>
                            ) : (
                                <RichHtmlEditor
                                    ref={rightEditorRef}
                                    value={rightContent}
                                    onChange={handleContentChange}
                                    onMouseUp={handleSelection}
                                    darkMode={darkMode}
                                    height={splitDirection === PANEL_ORIENTATIONS.HORIZONTAL ? 'calc(100vh - 230px)' : 'calc(50vh - 160px)'}
                                    onHighlightClick={(hid) => { setActiveHighlightId(hid); const h = highlights.find(x => x.id === hid); setCommentText(h?.comment || ''); setShowCommentDialog(true); }}
                                    compact={splitDirection === PANEL_ORIENTATIONS.VERTICAL}
                                    fontSize={targetFontSize}
                                    readOnly={
                                        sessionReadOnly ||
                                        !userCanEdit ||
                                        (rightActiveTab < availableTranslations.length &&
                                        availableTranslations[rightActiveTab] &&
                                        (availableTranslations[rightActiveTab].status === 'IN_PROGRESS' ||
                                            availableTranslations[rightActiveTab].status === 'PENDING'))
                                    }
                                />
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* Download options menu */}
            <Menu
                anchorEl={downloadMenuAnchorEl}
                open={Boolean(downloadMenuAnchorEl)}
                onClose={handleCloseDownloadMenu}
            >
                <MenuItem onClick={handleDownloadAsPDF}>
                    <PictureAsPdfIcon fontSize="small" sx={{ mr: 1 }} />
                    Download as PDF
                </MenuItem>
                <MenuItem onClick={handleDownloadAsDocx}>
                    <DescriptionIcon fontSize="small" sx={{ mr: 1 }} />
                    Download as DOCX
                </MenuItem>
                <MenuItem onClick={handleDownloadAsText}>
                    <TextSnippetIcon fontSize="small" sx={{ mr: 1 }} />
                    Download as Text
                </MenuItem>
            </Menu>

            {/* Translation dialog */}
            <Dialog open={showTranslationDialog} onClose={() => setShowTranslationDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Translation</DialogTitle>
                <DialogContent sx={{ pb: 0 }}>
                    <Tabs value={translateDialogTab} onChange={(_, v) => setTranslateDialogTab(v)} sx={{ mb: 2 }}>
                        <Tab label="AI Translate" icon={<TranslateIcon fontSize="small" />} iconPosition="start" />
                        <Tab label="Upload Existing" icon={<CloudUploadIcon fontSize="small" />} iconPosition="start" />
                    </Tabs>

                    <Box sx={{ mt: 2, mb: 2, p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
                        <Typography variant="caption" color="text.secondary">Source Language</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>{textFile?.sourceLanguage}</Typography>
                    </Box>

                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Target Language</InputLabel>
                        <Select
                            value={translationLanguage}
                            onChange={(e) => setTranslationLanguage(e.target.value)}
                            label="Target Language"
                        >
                            {SUPPORTED_LANGUAGES.map((language) => (
                                <MenuItem
                                    key={language.code}
                                    value={language.code}
                                    disabled={language.code === sourceLanguage}
                                >
                                    {language.name}
                                    {availableTranslations.some((t) => t.code === language.code) &&
                                        ' (Will update existing translation)'}
                                    {language.code === sourceLanguage && ' (Source language)'}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {translateDialogTab === 1 && (
                        <Box sx={{ mt: 2 }}>
                            <Button
                                component="label"
                                variant="outlined"
                                startIcon={<CloudUploadIcon />}
                                fullWidth
                            >
                                {uploadTranslationFile ? uploadTranslationFile.name : 'Select translation file (.html, .htm, .txt)'}
                                <input
                                    type="file"
                                    hidden
                                    accept=".html,.htm,.txt,.text"
                                    onChange={(e) => setUploadTranslationFile(e.target.files[0] || null)}
                                />
                            </Button>
                        </Box>
                    )}

                    {availableTranslations.some((t) => t.code === translationLanguage) && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            This will overwrite the existing translation for {translationLanguage}.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowTranslationDialog(false)}>Cancel</Button>
                    {translateDialogTab === 0 ? (
                        <Button
                            onClick={handleCreateTranslation}
                            color="primary"
                            startIcon={isTranslating ? <CircularProgress size={20} /> : <TranslateIcon />}
                            disabled={isTranslating || !translationLanguage || !sourceLanguage}
                        >
                            {isTranslating ? 'Translating...' : 'Translate'}
                        </Button>
                    ) : (
                        <Button
                            onClick={handleUploadTranslation}
                            color="primary"
                            variant="contained"
                            startIcon={isUploadingTranslation ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                            disabled={isUploadingTranslation || !translationLanguage || !uploadTranslationFile}
                        >
                            {isUploadingTranslation ? 'Uploading...' : 'Upload Translation'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Change Level dialog */}
            <Dialog open={showChangeLevelDialog} onClose={() => setShowChangeLevelDialog(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Change Level</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Language: <strong>{availableTranslations[rightActiveTab]?.code}</strong>
                    </Typography>
                    <FormControl fullWidth disabled={savingStage}>
                        <InputLabel>Process Stage</InputLabel>
                        <Select
                            label="Process Stage"
                            value={selectedLevelStage}
                            onChange={(e) => setSelectedLevelStage(e.target.value)}
                        >
                            {PROCESS_STAGES.map((stage) => (
                                <MenuItem
                                    key={stage}
                                    value={stage}
                                    disabled={!SELECTABLE_PROCESS_STAGES.includes(stage)}
                                >
                                    {stage}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowChangeLevelDialog(false)}>Cancel</Button>
                    <Button
                        onClick={handleSaveLevel}
                        color="primary"
                        variant="contained"
                        disabled={savingStage || !selectedLevelStage || selectedLevelStage.endsWith('_WIP')}
                        startIcon={savingStage ? <CircularProgress size={20} /> : null}
                    >
                        {savingStage ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add/Edit Comment Dialog */}
            <Dialog open={showCommentDialog} onClose={() => setShowCommentDialog(false)} fullWidth>
                <DialogTitle>Comment on Highlight</DialogTitle>
                <DialogContent>
                    <Typography variant="caption" sx={{ mb: 2, display: 'block' }}>
                        Target Text: <b>{highlights.find(h => h.id === activeHighlightId)?.highlightedText}</b>
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        placeholder="Type your comment here..."
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowCommentDialog(false)}>Cancel</Button>
                    <Button onClick={saveComment} variant="contained" color="primary">Save</Button>
                </DialogActions>
            </Dialog>

            {/* Floating Selection FAB */}
            <Popover
                open={Boolean(selectionAnchor)}
                anchorReference="anchorPosition"
                anchorPosition={selectionAnchor ? { top: selectionAnchor.top, left: selectionAnchor.left } : undefined}
                onClose={() => setSelectionAnchor(null)}
                hideBackdrop
                disableAutoFocus
                disableEnforceFocus
                disableRestoreFocus
                disableScrollLock
                sx={{ pointerEvents: 'none' }}
                PaperProps={{ 
                    sx: { 
                        pointerEvents: 'auto', 
                        p: 0, 
                        bgcolor: 'transparent', 
                        boxShadow: 'none',
                        overflow: 'visible'
                    } 
                }}
            >
                <Tooltip title="Add Comment">
                    <Fab 
                        size="small" 
                        color="primary" 
                        onClick={handleAddHighlight}
                        sx={{ width: 32, height: 32, minHeight: 32 }}
                    >
                        <ChatBubbleOutlineIcon sx={{ fontSize: 18 }} />
                    </Fab>
                </Tooltip>
            </Popover>

            {/* Comments Sidebar */}
            <Drawer
                anchor="right"
                open={showCommentsSidebar}
                onClose={() => setShowCommentsSidebar(false)}
                PaperProps={{
                    sx: { 
                        width: 350, 
                        bgcolor: darkMode ? '#1e1e1e' : '#fff', 
                        color: darkMode ? '#fff' : '#000',
                        boxShadow: -5
                    }
                }}
            >
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: darkMode ? '#2a2a2a' : '#f5f5f5' }}>
                    <Typography variant="h6">Comments & Highlights</Typography>
                    <IconButton onClick={() => setShowCommentsSidebar(false)} color="inherit">
                        <ExpandMoreIcon sx={{ transform: 'rotate(-90deg)' }} />
                    </IconButton>
                </Box>
                <Divider />
                <List sx={{ flexGrow: 1, overflowY: 'auto' }}>
                    {highlights.length === 0 ? (
                        <ListItem>
                            <ListItemText 
                                primary="No comments yet." 
                                secondary="Select text in the editor and click the chat bubble to add a comment."
                            />
                        </ListItem>
                    ) : (
                        highlights.map((h, idx) => (
                            <ListItem
                                key={h.id ?? `highlight-${idx}`}
                                divider
                                secondaryAction={
                                    <IconButton edge="end" onClick={() => deleteHighlight(h.id)} color="error" size="small">
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                }
                            >
                                <ListItemText
                                    primary={<Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>"{h.highlightedText}"</Typography>}
                                    secondary={
                                        <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                                            <Typography variant="body2" component="span" sx={{ color: darkMode ? '#e0e0e0' : 'text.primary', display: 'block' }}>
                                                {h.comment || <i style={{ opacity: 0.7 }}>No comment added</i>}
                                            </Typography>
                                            {(h.createdBy || h.createdAt) && (
                                                <Typography variant="caption" component="span" sx={{ color: darkMode ? '#888' : 'text.secondary', fontSize: '0.7rem', display: 'block' }}>
                                                    {h.createdBy && <span>{h.createdBy}</span>}
                                                    {h.createdBy && h.createdAt && <span> • </span>}
                                                    {h.createdAt && (
                                                        <span>{new Date(h.createdAt).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</span>
                                                    )}
                                                </Typography>
                                            )}
                                        </Box>
                                    }
                                />
                            </ListItem>
                        ))
                    )}
                </List>
                {highlights.length > 0 && (
                    <Box sx={{ p: 2 }}>
                        <Button fullWidth variant="outlined" color="error" onClick={clearAllComments} startIcon={<FormatClearIcon />}>
                            Clear All Comments
                        </Button>
                    </Box>
                )}
            </Drawer>

        </Box>
        <HelpOverlay helpItems={textEditorHelpItems} title="Text Editor Guide" buttonId="help-text-editor" />
        </>
    );
};

export default TextEditor;
